import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Share, Plus, Check, ArrowRight } from 'lucide-react';

// Hook to manage PWA install state globally
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsInstalled(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const installApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      setIsInstallable(false);
      return outcome === 'accepted';
    }
    return false;
  };

  return { isInstallable, isInstalled, isIOS, installApp };
};

// "Download App" link → Google Play Store
// (iOS build coming soon — same button works as a signal for iOS users too.)
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.emergent.occultkingapp0d7b58ff";

export const InstallAppButton = ({ className = "" }) => {
  // Rendered as a <button> (not <a>) because the global "emergent" badge-hider
  // CSS rule (a[href*="emergent"]) would otherwise hide this anchor due to the
  // Play Store package ID containing the substring "emergent".
  const openStore = () => window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
  return (
    <button
      type="button"
      onClick={openStore}
      className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-full font-medium text-sm hover:from-emerald-700 hover:to-green-700 transition-all shadow-lg hover:shadow-emerald-500/30 ${className}`}
      data-testid="download-app-btn"
    >
      {/* Google Play Store triangle logo */}
      <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
        <path fill="#FFD400" d="M3.61 1.81a2.05 2.05 0 0 0-.61 1.46v17.46c0 .57.22 1.1.61 1.46l11.92-11.96L3.61 1.81z" />
        <path fill="#FF3A44" d="M15.53 12.23l3.96-3.97-13.24-7.6c-.46-.27-1.05-.27-1.5-.04l10.78 11.61z" />
        <path fill="#00C8E0" d="M3.61 22.19c.45.23 1.04.23 1.5-.04l13.24-7.6-3.82-3.82L3.61 22.19z" />
        <path fill="#00B45A" d="M19.49 8.26l-3.96 3.97 3.82 3.82 4.24-2.45c.93-.53.93-1.92 0-2.45l-4.1-2.36z" />
      </svg>
      <span className="hidden sm:inline">Download App</span>
      <span className="sm:hidden">Get App</span>
    </button>
  );
};

// iOS Install Instructions Modal
const IOSInstallModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <img 
            src="/icons/icon-96x96.png" 
            alt="Occult King" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
          />
          <h2 className="text-xl font-bold text-gray-900">Install Occult King</h2>
          <p className="text-gray-500 text-sm mt-1">Add to your home screen in 2 easy steps</p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Share className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Step 1</p>
              <p className="text-gray-600 text-sm">Tap the <strong>Share</strong> button in Safari</p>
            </div>
          </div>

          <div className="flex justify-center">
            <ArrowRight className="w-5 h-5 text-gray-300 rotate-90" />
          </div>

          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Plus className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Step 2</p>
              <p className="text-gray-600 text-sm">Tap <strong>"Add to Home Screen"</strong></p>
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-colors"
        >
          Got it!
        </button>

        <style jsx>{`
          @keyframes slide-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out forwards;
          }
        `}</style>
      </div>
    </div>
  );
};

// Full Install Page Component
export const InstallAppPage = () => {
  const { isInstallable, isInstalled, isIOS, installApp } = usePWAInstall();
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    const success = await installApp();
    if (success) {
      setInstalled(true);
    }
    setInstalling(false);
  };

  if (isInstalled || installed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">App Installed!</h1>
          <p className="text-gray-300">You can now access Occult King from your home screen</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="/icons/icon-192x192.png" 
            alt="Occult King" 
            className="w-28 h-28 mx-auto mb-6 rounded-3xl shadow-2xl"
          />
          <h1 className="text-3xl font-bold text-white mb-2">Occult King</h1>
          <p className="text-gray-400">Install the app for the best experience</p>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 mb-6">
          <h2 className="text-white font-semibold mb-4">Why Install?</h2>
          <ul className="space-y-3">
            {[
              'Quick access from home screen',
              'Works offline',
              'Faster loading',
              'No browser UI - full screen experience',
              'Get notifications'
            ].map((feature, i) => (
              <li key={i} className="flex items-center gap-3 text-gray-300">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {isIOS ? (
          <div className="bg-white rounded-3xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 text-center">Install on iPhone/iPad</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <div className="flex items-center gap-2">
                  <Share className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Tap <strong>Share</strong> button below</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Tap <strong>"Add to Home Screen"</strong></span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            disabled={installing || !isInstallable}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {installing ? (
              <>
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Installing...
              </>
            ) : (
              <>
                <Download className="w-6 h-6" />
                Install App Now
              </>
            )}
          </button>
        )}

        {!isInstallable && !isIOS && (
          <p className="text-center text-gray-400 text-sm mt-4">
            Open in Chrome or Safari browser to install
          </p>
        )}
      </div>
    </div>
  );
};

export default InstallAppButton;
